<template>
  <div class="general-type-card d-flex align-center pa-3 mb-2">
    <span class="flex-grow-1">
      <span class="font-weight-bold d-block">{{ type.name }}</span>
      <span v-if="price !== null" class="text-caption">
        {{ formatCurrency(price) }} + {{ formatCurrency(fee) }} de taxa
      </span>
    </span>
    <!-- The same width on every card, whatever the length of its type's name -->
    <div class="control d-flex justify-end">
      <span v-if="type.current_batch == null || soldOut" class="text-caption">Esgotado</span>
      <t-btn
        v-else-if="exclusive"
        size="small"
        variant="tonal"
        :color="type.integration_tiers[0].integration.primary_color"
        @click="onAccessButtonClick"
      >
        Venda exclusiva
      </t-btn>
      <t-number-input
        v-else
        v-model="amount"
        hide-details
        density="compact"
        :max="max"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from "vue";
import formatCurrency from "@/filters/currency";
import type Event from "@/types/Event";
import type Sector from "@/types/Sector";
import type TicketType from "@/types/TicketType";
import TBtn from "@/components/core/TBtn.vue";
import TNumberInput from "@/components/core/TNumberInput.vue";
import { useTicketAccess } from "@/composables/useTicketAccess";
import Fee from "@/utils/fees";
import { isSoldOut } from "@/utils/ticketType";

interface Props {
  sector: Sector;
  type: TicketType;
  event: Event;
  /** How far the stepper goes. */
  max: number;
  modelValue?: number;
}

const { sector, type, event, max, modelValue = 0 } = defineProps<Props>();

const emit = defineEmits<{
  "update:modelValue": [value: number];
}>();

defineOptions({
  name: "GeneralTypeCard",
});

const { exclusive, onAccessButtonClick } = useTicketAccess(() => type, () => event);

const soldOut = computed<boolean>(() => isSoldOut(sector, type));

/** The current batch's, or with none the last closed one's. Null renders no price at all. */
const price = computed<number | null>(() => type.display_batch?.price ?? null);

const fee = computed<number>(() => Fee.calculate(price.value, event.convenience_fee, event.convenience_fee_mode));

const amount = computed<number>({
  get: () => modelValue,
  set: value => emit("update:modelValue", value),
});
</script>

<style scoped>
.control {
  flex: none;
  width: 136px;
  margin-left: 12px;
}

.general-type-card {
  background: white;
  border: 1px solid #e3e3e3;
  border-radius: 8px;
}
</style>
